import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import {
  generatePairings,
  generateAllRounds,
  createManualRound,
  addManualPairing,
  getTournament,
  ApiError,
} from '@/lib/api';
import { showErrorToast } from '@/lib/toast';
import type { Round, Tournament, RoundBye } from '@swiss-magic/shared-types';

type Input = {
  tournamentId: string | undefined;
  rounds: Round[];
  setRounds: Dispatch<SetStateAction<Round[]>>;
  setTournament: Dispatch<SetStateAction<Tournament | null>>;
  selectedRound: number;
  setSelectedRound: Dispatch<SetStateAction<number>>;
  isKo: boolean;
  godMode: boolean;
  noValidPairing: boolean;
  setNoValidPairing: Dispatch<SetStateAction<boolean>>;
  handleActionError: (err: unknown, msg: string) => void;
};

export function useRoundGeneration({
  tournamentId,
  rounds,
  setRounds,
  setTournament,
  selectedRound,
  setSelectedRound,
  isKo,
  godMode,
  setNoValidPairing,
  handleActionError,
}: Input) {
  const { t } = useTranslation('director');
  const [generating, setGenerating] = useState(false);
  const [showAddPairing, setShowAddPairing] = useState(false);
  const [showFallbackNotice, setShowFallbackNotice] = useState(false);

  useEffect(() => {
    setShowAddPairing(false);
  }, [tournamentId]);

  async function handleGenerate(
    forceWithIncomplete?: boolean,
    byes?: RoundBye[],
  ) {
    if (!tournamentId) return;
    setGenerating(true);
    try {
      const round = await generatePairings(
        tournamentId,
        forceWithIncomplete,
        byes,
      );
      setRounds((prev) => [...prev, round]);
      setSelectedRound(rounds.length); // select the new round
      // Show notice only on the first fallback round (not on subsequent ones)
      const previouslyUsedFallback = rounds.some((r) => r.usedFallbackPairing);
      setShowFallbackNotice(!!round.usedFallbackPairing && !previouslyUsedFallback);
      const t = await getTournament(tournamentId);
      setTournament(t);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === 'validation.rounds.no_valid_pairing' && !isKo) {
        setNoValidPairing(true);
        showErrorToast(t('rounds_page.toast.no_valid_pairing'));
      } else {
        handleActionError(err, t('rounds_page.toast.generate_pairings_failed'));
      }
    } finally {
      setGenerating(false);
    }
  }

  async function handleGenerateAllRounds() {
    if (!tournamentId) return;
    setGenerating(true);
    try {
      const allRounds = await generateAllRounds(tournamentId);
      setRounds(allRounds);
      setSelectedRound(0); // select round 1
      const t = await getTournament(tournamentId);
      setTournament(t);
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.generate_rounds_failed'));
    } finally {
      setGenerating(false);
    }
  }

  async function handleCreateManualRound() {
    if (!tournamentId) return;
    setGenerating(true);
    try {
      const round = await createManualRound(tournamentId, godMode);
      setRounds((prev) => [...prev, round]);
      setSelectedRound(rounds.length);
      const t = await getTournament(tournamentId);
      setTournament(t);
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.create_round_failed'));
    } finally {
      setGenerating(false);
    }
  }

  async function handleAddManualPairing(whitePlayerId: string, blackPlayerId: string | null) {
    const round = rounds[selectedRound];
    if (!round || !tournamentId) return;
    try {
      const updated = await addManualPairing(tournamentId, round.id, whitePlayerId, blackPlayerId);
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
      setShowAddPairing(false);
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.add_pairing_failed'));
    }
  }

  return {
    generating,
    showAddPairing,
    setShowAddPairing,
    showFallbackNotice,
    setShowFallbackNotice,
    handleGenerate,
    handleGenerateAllRounds,
    handleCreateManualRound,
    handleAddManualPairing,
  };
}
